import { MouseEvent, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { RecoilRoot } from 'recoil';

import { AxiosError } from 'axios';
import { Box } from '@mui/material';

import { queries, requests } from 'RestAPIs/rapini';
import { ExpenseClaimResponse } from 'generated/index';

import { DataGrid } from 'components/shared/DataGrid';
import Header from './components/Header';
import { PlanUnitMix } from 'components/types';
import { ClaimsAction, useColumnDefinitions } from '.';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import { WarningDialog } from 'components/roster-portal/activity-planning/components';
import { Footer } from './components';
import LoadingOverlay from 'components/shared/LoadingOverlay';
import { getCurrentlyLoggedInEmployeeId } from 'utils/misc';
import WarningDialogAdvanced from 'components/roster-portal/activity-planning/components/WarningDialog/WarningDialogAdvanced';
import { faCheck, faClose } from 'vendors/@fortawesome/pro-light-svg-icons';
import TravelDetailDialog from './components/TravelDetailDialog';
import { useNavParams } from 'hooks';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

interface TravelExpenseClaimsProps {
  selectedEmployeeId?: number;
  page?: 'EMPLOYEE_CARD' | 'EMPLOYEE_PORTAL' | 'START';
  disableActions?: boolean;
}

const TravelExpenseClaims = ({
  page = 'START',
  selectedEmployeeId,
  disableActions = false
}: TravelExpenseClaimsProps) => {
  const columns = useColumnDefinitions();
  const { params } = useNavParams();
  const { t } = useTranslation('common');
  const [selectedPlanUnits, setSelectedPlanUnits] = useState<PlanUnitMix[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedRows, setSelectedRows] = useState<ExpenseClaimResponse[]>([]);
  const [dayOfMonth, setDayOfMonth] = useState<Date | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState<boolean>(false);
  const [showWarningDialog, setShowWarningDialog] = useState<
    [boolean, ClaimsAction | null]
  >([false, null]);
  const [showRejectDialog, setShowRejectDialog] = useState<
    [boolean, ClaimsAction | null]
  >([false, null]);
  const [activeClaim, setActiveClaim] = useState<ExpenseClaimResponse | null>(
    null
  );

  const isOverview = !page || page === 'START';
  const isEmployeeCard = page === 'EMPLOYEE_CARD';
  const isEmployeePortal = page === 'EMPLOYEE_PORTAL';
  const isEmployee = isEmployeeCard || isEmployeePortal;

  const loggedInUserEmployeeId = +getCurrentlyLoggedInEmployeeId();

  const startDate = dayjs.utc(selectedDate).startOf('month').toISOString();
  const endDate = dayjs
    .utc(selectedDate)
    .endOf('month')
    .add(1, 'hour')
    .toISOString();

  const queryClient = useQueryClient();

  // Expense claims by employee
  const getExpenseClaimsByEmployee = () =>
    queries.useFindExpenseClaimsByEmployee(
      startDate,
      endDate,
      isEmployeePortal
        ? loggedInUserEmployeeId
        : (selectedEmployeeId as number),
      {
        enabled: isEmployee && !!(loggedInUserEmployeeId || selectedEmployeeId)
      }
    );

  // Expense claims by organizational unit
  const getExpenseClaimsByOrganizationalUnits = () =>
    queries.useFindExpenseClaimsByOrganizationalUnits(
      startDate,
      endDate,
      //@ts-expect-error TODO: Rapini should accept array of ids
      selectedPlanUnits?.map((p: PlanUnitMix) => p.id).join(','),
      {
        enabled: !isEmployee && selectedPlanUnits.length > 0
      }
    );

  const {
    data = [],
    refetch: refetchExpenseClaims,
    isLoading
  } = isEmployee
    ? getExpenseClaimsByEmployee()
    : getExpenseClaimsByOrganizationalUnits();

  const rows = data.map((ecr: ExpenseClaimResponse) => ({
    ...ecr,
    transportationTypeName: ecr.transportationType?.name,
    disableSelection: !ecr.canBeApproved
  }));

  const totalKm =
    rows.reduce(
      (acc, row) =>
        acc +
        row.routes.reduce((acc, route) => acc + route.distanceInMeters, 0),
      0
    ) / 1000;

  const approveClaims = async () => {
    const { approveExpenseClaims } = requests;

    try {
      await approveExpenseClaims({
        expenseClaimIds: selectedRows
          .filter(({ canBeApproved }) => canBeApproved)
          .map(({ id }) => id)
      });

      queryClient.invalidateQueries(
        isEmployee
          ? 'findExpenseClaimsByEmployee'
          : 'findExpenseClaimsByOrganizationalUnits'
      );

      showSuccessSnackbar();
    } catch (err) {
      showErrorSnackbar((err as AxiosError)?.message);
    } finally {
      setShowWarningDialog([false, null]);
      setSelectedRows([]);
    }
  };

  const rejectClaims = async (formData) => {
    const { rejectExpenseClaims } = requests;

    try {
      await rejectExpenseClaims({
        expenseClaimIds: selectedRows.map(({ id }) => id),
        statusNote: formData.remark
      });

      queryClient.invalidateQueries(
        isEmployee
          ? 'findExpenseClaimsByEmployee'
          : 'findExpenseClaimsByOrganizationalUnits'
      );

      showSuccessSnackbar();
    } catch (err) {
      showErrorSnackbar((err as AxiosError)?.message);
    } finally {
      setShowRejectDialog([false, null]);
      // Clear selected rows only after dialog closes
      setSelectedRows([]);
    }
  };

  const handleAction = (action: ClaimsAction) => {
    switch (action) {
      case ClaimsAction.APPROVE:
        setShowWarningDialog([true, ClaimsAction.APPROVE]);
        break;
      case ClaimsAction.REJECT:
        setShowRejectDialog([true, ClaimsAction.REJECT]);
        break;
      default:
        break;
    }
  };

  const handleCloseDetailDialog = () => {
    setShowDetailDialog(false);
    setActiveClaim(null);
  };

  const handleActionClick = (
    id: string | number,
    row: ExpenseClaimResponse,
    action: ClaimsAction,
    event?: MouseEvent<HTMLButtonElement>
  ) => {
    event?.preventDefault();
    event?.stopPropagation();

    // Ensure the row is selected
    row &&
      !selectedRows.some((r) => r.id === row.id) &&
      setSelectedRows((prev) => [...prev, row]);

    // Set the active claim
    setActiveClaim(rows.find((r) => r.id === id)!);

    // Trigger the action (Approve or Reject)
    handleAction?.(action);
  };

  useEffect(() => {
    if (selectedPlanUnits.length > 0) refetchExpenseClaims();
  }, [selectedPlanUnits, selectedDate, page]);

  // Update the selected date when the URL changes
  useEffect(() => {
    if (params?.date && dayjs(params.date, 'DD-MM-YYYY').isValid()) {
      const newDate = dayjs(params.date, 'DD-MM-YYYY').toDate();
      if (newDate.getTime() !== dayOfMonth?.getTime()) {
        setDayOfMonth(newDate);
        setSelectedDate(newDate);
      }
    }
  }, [params?.date, dayOfMonth]);

  return (
    <RecoilRoot>
      <Header
        onAction={handleAction}
        onSelectPlanUnit={setSelectedPlanUnits}
        selectedDate={selectedDate}
        onSelectedDate={setSelectedDate}
        selection={selectedRows}
        showActions={isOverview}
        showPlanUnitSelector={isOverview}
      />
      <Box height="100%" overflow="auto">
        <DataGrid<ExpenseClaimResponse>
          rows={rows}
          columns={columns}
          pageSize={50}
          rowsPerPageOptions={[5]}
          checkboxSelection={rows.some(
            ({ disableSelection }) => !disableSelection
          )}
          footer={
            isEmployeeCard ? (
              <Footer>
                <Box mr={1}>
                  {t('travelExpenseClaims.totalKm')}: {totalKm}
                </Box>
              </Footer>
            ) : null
          }
          customSelectionModel={selectedRows.map(({ id }) => id)}
          onSelectionChange={(_, rows = []) => {
            setSelectedRows(rows);
          }}
          onViewClick={(
            id: string | number,
            _row,
            event?: MouseEvent<HTMLButtonElement>
          ) => {
            event?.preventDefault();
            event?.stopPropagation();
            setActiveClaim(rows.find((r) => r.id === id)!);
            setShowDetailDialog(true);
          }}
          customActions={(id, row) => [
            {
              tooltipTitle: t('travelExpenseClaims.actions.APPROVE'),
              icon: faCheck,
              onClick: (id, _row, event) =>
                row && handleActionClick(id, row, ClaimsAction.APPROVE, event),
              hidden: disableActions || row?.status !== 'SUBMITTED'
            },
            {
              tooltipTitle: t('travelExpenseClaims.actions.REJECT'),
              icon: faClose,
              onClick: (id, _row, event) =>
                row && handleActionClick(id, row, ClaimsAction.REJECT, event),
              hidden: disableActions || row?.status !== 'SUBMITTED'
            }
          ]}
          initialState={{
            sorting: {
              sortModel: [{ field: 'date', sort: 'asc' }]
            }
          }}
          sx={{
            mt: 1,
            // Hide the checkbox if the row/input is disabled
            '.MuiCheckbox-root input[type="checkbox"]:disabled + *': {
              display: 'none'
            }
          }}
        />
      </Box>

      <WarningDialog
        title={t('travelExpenseClaims.warning', {
          action: t(`travelExpenseClaims.actions.${showWarningDialog[1]}`)
        })}
        open={showWarningDialog[0]}
        onSubmit={() => approveClaims()}
        onCancel={() => {
          setShowWarningDialog([false, null]);
        }}
      />

      <WarningDialogAdvanced
        title={t('travelExpenseClaims.warning', {
          action: t(`travelExpenseClaims.actions.${showRejectDialog[1]}`)
        })}
        open={showRejectDialog[0]}
        selectedRows={selectedRows}
        onSubmit={(formData) => rejectClaims(formData)}
        onCancel={() => {
          setShowRejectDialog([false, null]);
        }}
      />

      <TravelDetailDialog
        claim={activeClaim}
        isOpen={showDetailDialog}
        onClose={handleCloseDetailDialog}
      />

      <LoadingOverlay loading={isLoading} />
    </RecoilRoot>
  );
};

export default TravelExpenseClaims;
